import { AnalyticsProvider } from "@asurion-core/eventualize-react";
import { useTimelineStore } from "@asurion-core/expert-workspace-timeline";
import { getAppConfigSingleton } from "@expert/app-config";
import { ExpertUniversityConfigurationProvider, partnerEnabledForExpertUniversity } from "@expert/expert-university";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import {
    SalesContextProvider,
    useCallDetailsStore,
    useCallIntroStore,
    useDetectedCuesStore,
    useIntegratedFlowStore,
    useOfferChecklistStore,
    useProductOfferStore,
    useVerificationFlowStore,
    useVerifiedCustomerStore,
} from "@expert/sales-tools";
import { useGuideStore } from "@expert/solve-tools";
import { Flex } from "@mantine/core";
import { useWindowEvent } from "@mantine/hooks";
import { type JSX, type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";

import { useAutopilotEventsListener } from "@/amazon-connect/chat/hooks/useAutopilotEventsListener";
import { useNeglectionEventListener } from "@/amazon-connect/chat/hooks/useNeglectionEventListener";
import { useCallCategoryListener } from "@/call-controls/hooks/useCallCategoryListener";
import { useBuildSalesContext } from "@/components/workspace/hooks/useBuildSalesContext";
import { useDynamicSmartOfferListenerWrapper } from "@/components/workspace/hooks/useDynamicSmartOfferListenerWrapper";
import { useSetGaiaSessionContext } from "@/components/workspace/hooks/useSetGaiaSessionContext";
import { useTrackVersion } from "@/components/workspace/hooks/useTrackVersion";
import { WorkspaceContent } from "@/components/workspace/WorkspaceContent";
import { env } from "@/config";
import { useEnrollmentEventListener } from "@/customer-panel/hooks/useEnrollEventListener";
import { useLeaderboardWebSocketBridge } from "@/customer-panel/hooks/useLeaderboardWebSocketBridge";
import { EventEmitter } from "@/dev-tools";
import { useTechCuesListener } from "@/gaia-client/hooks/useTechCuesListener";
import { MorningPaper, useShouldShowMorningPaper } from "@/morning-paper";
import { usePartnerConfig, usePartnerFeatureConfigured } from "@/partner/usePartnerConfig";
import { useChallengeEventListener } from "@/sales-tools/challenges/hooks/useChallengeEventListener";
import { useRapportBuildingListener } from "@/sales-tools/rapport-building/useRapportBuildingListener";
import { useSalesChecklistEventListener } from "@/sales-tools/sales-checklist/useSalesChecklistEventListener";
import {
    DevDebugTools,
    isChatTask,
    isSimulatedVoiceTask,
    isTrainingTask,
    isVoiceTask,
    LoggerContext,
    sdkEventBus,
    useActiveTask,
    useAgentSdk,
    useAgentStore,
    useCallbackState,
    useLogger,
    usePartner,
    useSession,
} from "@/sdk";
import { useSessionSummaryListener } from "@/session-summary/api/useSessionSummaryListener";
import { isNullish, userCache, useFeatureFlag } from "@/shared-utils";
import { useTroubleshootingStore } from "@/troubleshoot";

import { WorkspaceHeader } from "../header";
import { useInitExpertMonitoringTools } from "./hooks/useExpertMonitoringTools";
import { useUndesiredTabsCloser } from "./hooks/useUndesiredTabsCloser";
import { useWrapupToasts } from "./hooks/useWrapupToasts";
import styles from "./workspace.module.css";

/**
 * Workspace wrapper that provides LoggerContext with session context.
 * The actual workspace logic is in WorkspaceInner.
 */
export function Workspace({
    isAuthRedirectNeededRef,
    isMorningPaperEnabled,
}: {
    isAuthRedirectNeededRef: RefObject<boolean>;
    isMorningPaperEnabled: boolean;
}): JSX.Element {
    useTrackVersion();
    const activeTask = useActiveTask();
    const session = useSession();
    const partner = usePartner();
    const currentActivity = useAgentStore((state) => state.activity);

    // Derive callSid only for voice tasks, chatId only for chat tasks
    const callSid = isVoiceTask(activeTask) ? activeTask.agentCallId : undefined;
    const chatId = isChatTask(activeTask) ? activeTask.chatId : undefined;

    // isTrainingMode is bound here so every descendant log is tagged for filtering training-only errors.
    // Note: this only tags logs made via useLogger (React tree). Module-scope getLogger() calls won't include it.
    const isTrainingMode = currentActivity === "Training" || currentActivity === "Residency";

    const logger = useLogger({
        module: "Workspace",
        sessionId: session.id,
        partner,
        callSid,
        chatId,
        isTrainingMode,
    });

    return (
        <LoggerContext value={logger}>
            <WorkspaceInner
                isAuthRedirectNeededRef={isAuthRedirectNeededRef}
                activeTask={activeTask}
                session={session}
                partner={partner}
                isMorningPaperEnabled={isMorningPaperEnabled}
            />
        </LoggerContext>
    );
}

interface WorkspaceInnerProps {
    isAuthRedirectNeededRef: RefObject<boolean>;
    activeTask: ReturnType<typeof useActiveTask>;
    session: ReturnType<typeof useSession>;
    partner: string;
    isMorningPaperEnabled: boolean;
}

/**
 * Inner workspace component that has access to the logger context.
 */
function WorkspaceInner({
    isAuthRedirectNeededRef,
    activeTask,
    session,
    partner,
    isMorningPaperEnabled,
}: WorkspaceInnerProps) {
    const logger = useLogger({ module: "Workspace" });
    const navigate = useNavigate();
    const sdk = useAgentSdk();
    const agentId = useAgentStore((state) => state.agentId);
    const currentActivity = useAgentStore((state) => state.activity);
    const { isTroubleshooting, setIsTroubleshooting } = useTroubleshootingStore();
    const { data: isInExpertUniversityPilot } = useFeatureFlag("isInExpertUniversityPilot");
    const sessionId = session.id;

    const isCustomerSession = session.kind === "with-customer";
    const activeTaskIdentities = activeTask ? { TaskId: activeTask.id } : {};
    const activeTaskExtra = activeTask
        ? {
              taskType: activeTask.type,
              partner: activeTask.partner,
          }
        : {};

    const {
        simplifiedSummary,
        callControlsEnabled,
        customerPanelEnabled,
        logOutOnOfflineAgentActivity,
        isRealtimeTranscriptEnabled,
        outboundCall,
    } = usePartnerFeatureConfigured();
    const { technicalIssues } = usePartnerConfig();
    const isExpertUniversityEnabled =
        partnerEnabledForExpertUniversity(partner, env.channel, isInExpertUniversityPilot) &&
        (currentActivity === "Training" || currentActivity === "Residency");
    const callbackState = useCallbackState();

    const hasActiveVoiceTask = isVoiceTask(activeTask);
    const isOutboundEnabled =
        outboundCall || (technicalIssues?.enableOutboundCalls && currentActivity === technicalIssues.agentActivity);
    const hasCallbackWaiting = useMemo(() => {
        return Boolean(
            !isNullish(callbackState?.callbackDelay) &&
            callbackState.scheduledFor &&
            callbackState.callbackType === "CallbackNow",
        );
    }, [callbackState]);
    const {
        features: { isMicroLearningV2Enabled, logoutPendingDuration, isChallengeEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const [pendingLogout, setPendingLogout] = useState(false);
    const auth = useAuth();

    const [hasCallEnded, setHasCallEnded] = useState(false);

    const activeTrainingTask = useMemo(() => {
        return isTrainingTask(activeTask) ? activeTask : null;
    }, [activeTask]);

    const showExpertUniversity = !activeTask && isExpertUniversityEnabled;

    const activeExpertUniversityTask = useMemo(() => {
        return isSimulatedVoiceTask(activeTask) ? activeTask : null;
    }, [activeTask]);
    const showRealtimeTranscript = Boolean(
        (isRealtimeTranscriptEnabled && isCustomerSession) || activeExpertUniversityTask,
    );

    useTechCuesListener();
    useCallCategoryListener(sessionId);
    useSetGaiaSessionContext();
    const shouldListenForSessionSummary = simplifiedSummary && env.channel !== "chat";
    useSessionSummaryListener(shouldListenForSessionSummary);
    useSalesChecklistEventListener();
    useRapportBuildingListener();
    useChallengeEventListener();
    useDynamicSmartOfferListenerWrapper();
    useEnrollmentEventListener();
    useLeaderboardWebSocketBridge();
    useNeglectionEventListener();
    useAutopilotEventsListener();

    useEffect(() => {
        const logoutTimeout = setTimeout(() => {
            if (pendingLogout) {
                void auth.revokeTokens().catch((_) => {});
                sdk.logout?.();
                localStorage.clear();
                sessionStorage.clear();
                void navigate("/logged-out");
            }
        }, logoutPendingDuration);

        return () => clearTimeout(logoutTimeout);
        // oxlint-disable-next-line exhaustive-deps
    }, [pendingLogout, sdk?.logout, logoutPendingDuration, navigate]);

    useEffect(() => {
        const agentOfflineEvent = sdkEventBus.on("agent_activity_changed", ({ previousActivity, activity }) => {
            // ignore event if activity is unchanged (e.g. updatedAt changed but activity didn't)
            if (activity === previousActivity) return;
            if (logOutOnOfflineAgentActivity && activity === sdk.offlineAgentActivity) {
                setPendingLogout(true);
            }
        });

        const technicalIssuesEvent = sdkEventBus.on("technical_issues", () => {
            logger.info("Troubleshooting | open");
            setIsTroubleshooting(true);
        });

        const sessionChangedEvent = sdkEventBus.on("session_changed", ({ action }) => {
            if (action === "end" && isMicroLearningV2Enabled) {
                logger.info("MicroLearning triggered");
                setHasCallEnded(true);
            }
        });
        return () => {
            agentOfflineEvent();
            technicalIssuesEvent();
            sessionChangedEvent();
        };
        // oxlint-disable-next-line exhaustive-deps
    }, [logger, setIsTroubleshooting, isMicroLearningV2Enabled]);

    useWrapupToasts();

    useWindowEvent("beforeunload", (ev) => {
        // show confirmation dialog while with a customer (e.g. on a call) unless we're redirecting to re-authenticate
        if (!isCustomerSession || isAuthRedirectNeededRef.current) return;
        ev.preventDefault();
        ev.returnValue = "";
        return "";
    });

    useInitExpertMonitoringTools();

    // We are gradually introducing a new context. For now, this is launched darkly
    // to prove there are no side effects or regressions.
    const salesContext = useBuildSalesContext();

    useEffect(() => {
        // Check if partner config exists in the appConfigSingleton on partner change and warn once if not
        if (partner && partner !== "unknown") {
            const appConfig = getAppConfigSingleton();
            if (!appConfig.hasPartnerConfig(partner)) {
                logger.warn(`Partner configuration not found for '${partner}', 'unknown' configuration will be used.`);
            }
        }
    }, [logger, partner]);

    useEffect(() => {
        if (env.channel === "chat") {
            useTimelineStore.getState().setUseDefaultTimelineKey(false);
        }
    }, []);

    /**
     * Manually rehydrates persisted Zustand stores when entering a customer session.
     * Skips entirely if any listed store has already hydrated
     * These stores have `skipHydration: true` in their persist config, which disables automatic rehydration.
     *
     * TODO: Codeowners should audit each store and consider removing `skipHydration: true`. Manual rehydration here probably isn't needed when using map-based stores.
     */
    useEffect(() => {
        if (session.metadata.status === "ended" || session.kind !== "with-customer") {
            return;
        }
        const persistApis = [
            useVerificationFlowStore.persist,
            useCallDetailsStore.persist,
            useProductOfferStore.persist,
            useVerifiedCustomerStore.persist,
            useGuideStore.persist,
            useOfferChecklistStore.persist,
            useDetectedCuesStore.persist,
            useIntegratedFlowStore.persist,
            useCallIntroStore.persist,
        ];
        // Skips rehydrating all stores if any one has already hydrated to avoid redundant storage reads.
        if (persistApis.some((persistedStore) => persistedStore.hasHydrated())) {
            return;
        }
        logger.trace("Rehydrating stores manually for customer session");
        for (const persistedStore of persistApis) {
            void persistedStore.rehydrate();
        }
    }, [logger, session.metadata.status, session.kind, session.id]);

    useUndesiredTabsCloser();

    const containerRef = useRef(null);
    const shouldShowMorningPaper = useShouldShowMorningPaper(isMorningPaperEnabled);

    return (
        <Flex ref={containerRef} className={styles.container}>
            {env.isProdMode ? null : (
                <>
                    <DevDebugTools />
                    {activeTask && <EventEmitter partner={partner} sessionId={sessionId} />}
                </>
            )}
            <ExpertUniversityConfigurationProvider userCache={userCache} partner={partner}>
                <AnalyticsProvider
                    dispatcher={(dispatcher) =>
                        dispatcher
                            .withIdentities({
                                WorkerId: agentId,
                                SessionId: sessionId,
                                ...activeTaskIdentities,
                            })
                            .withExtra({ partner, ...activeTaskExtra })
                    }
                >
                    <WorkspaceHeader />

                    {shouldShowMorningPaper ? (
                        <MorningPaper />
                    ) : (
                        <SalesContextProvider value={salesContext}>
                            <WorkspaceContent
                                activeExpertUniversityTask={activeExpertUniversityTask}
                                activeTrainingTask={activeTrainingTask}
                                callControlsEnabled={callControlsEnabled}
                                hasActiveVoiceTask={hasActiveVoiceTask}
                                hasCallbackWaiting={hasCallbackWaiting}
                                isOutboundEnabled={isOutboundEnabled}
                                isRealCustomerPanelEnabledForTraining={isExpertUniversityEnabled}
                                challengeEnabled={isChallengeEnabled}
                                currentActivity={currentActivity}
                                customerPanelEnabled={customerPanelEnabled}
                                customerSession={isCustomerSession}
                                hasCallEnded={hasCallEnded}
                                microLearningV2Enabled={isMicroLearningV2Enabled}
                                showRealtimeTranscript={showRealtimeTranscript}
                                sdk={sdk}
                                session={session}
                                showExpertUniversity={showExpertUniversity}
                                troubleshooting={isTroubleshooting}
                            />
                        </SalesContextProvider>
                    )}
                </AnalyticsProvider>
            </ExpertUniversityConfigurationProvider>
        </Flex>
    );
}
